import { useEffect, useRef, useState } from "react";
import { Upload, Image as ImageIcon, X, Loader2, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { api, resolveUrl, formatApiErrorDetail } from "../api";

export const ImageUpload = ({ value, onChange, label = "Image", testId = "image-upload" }) => {
  const [uploading, setUploading] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlValue, setUrlValue] = useState(value || "");
  const inputRef = useRef(null);

  useEffect(() => {
    setUrlValue(value || "");
  }, [value]);

  const onPick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/cms/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const previewUrl = value ? resolveUrl(value) : null;

  return (
    <div data-testid={testId}>
      <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-400 mb-2">{label}</div>
      <div className="bento-card p-3 flex flex-col sm:flex-row gap-3">
        <div className="w-full sm:w-32 h-32 bg-black/40 border border-white/10 grid place-items-center overflow-hidden shrink-0">
          {previewUrl ? (
            <img src={previewUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <ImageIcon className="h-6 w-6 text-zinc-600" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          {showUrlInput ? (
            <div className="flex flex-col gap-2">
              <input
                type="text"
                value={urlValue}
                onChange={(e) => setUrlValue(e.target.value)}
                placeholder="https://… or /api/uploads/…"
                className="bg-transparent border-b-2 border-white/15 focus:border-brand-light outline-none py-2 text-sm text-white placeholder:text-zinc-600 transition-colors"
                data-testid={`${testId}-url-input`}
              />
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => { onChange(urlValue); setShowUrlInput(false); }}
                  className="text-xs tracking-[0.2em] uppercase font-bold text-brand-light hover:text-white"
                >
                  Use URL
                </button>
                <button
                  type="button"
                  onClick={() => setShowUrlInput(false)}
                  className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 hover:text-white"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="text-xs text-zinc-400 break-all line-clamp-2 mb-3 min-h-[2.5em]">
                {value ? value : "No image set."}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={onPick}
                  disabled={uploading}
                  className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-white bg-brand hover:bg-brand-hover px-3 py-2 transition-colors"
                  data-testid={`${testId}-upload-button`}
                >
                  {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                  Upload
                </button>
                <button
                  type="button"
                  onClick={() => setShowUrlInput(true)}
                  className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-zinc-300 hover:text-white border border-white/15 hover:border-brand-light px-3 py-2 transition-colors"
                >
                  <LinkIcon className="h-3.5 w-3.5" /> Use URL
                </button>
                {value && (
                  <button
                    type="button"
                    onClick={() => onChange("")}
                    className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 hover:text-red-400 px-3 py-2 transition-colors"
                  >
                    <X className="h-3.5 w-3.5" /> Remove
                  </button>
                )}
              </div>
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            onChange={onFile}
            className="hidden"
          />
        </div>
      </div>
    </div>
  );
};
